import { useCallback, useEffect, useRef, useState } from 'react'
import { getLatestHacksByActivity } from '@domain/features/pd-hacks/pd-hacks-feature'
import type { HackSymptom } from '@domain/models/pd-hacks'
import { getContentRefreshPolicy } from '@presentation/hooks/content-refresh-policy'
import { useAutoRefreshOnForeground } from '@presentation/hooks/useAutoRefreshOnForeground'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'

export function useLatestHacks(count = 3) {
  const ssrHacks = useSsrPageData()?.home?.latestHacks?.slice(0, count)
  const hasSsrHacks = ssrHacks !== undefined
  const [hacks, setHacks] = useState<HackSymptom[]>(ssrHacks ?? [])
  const [loading, setLoading] = useState(!ssrHacks)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)
  const refreshPolicy = getContentRefreshPolicy('home')

  const {
    triggerRefresh,
    lastSuccessAt,
    isRefreshing,
  } = useAutoRefreshOnForeground({
    refresh: async ({ reason }) => {
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      const showInitialLoading = reason === 'mount' && hacks.length === 0

      if (showInitialLoading) {
        setLoading(true)
      }

      try {
        setError(null)
        const result = await getLatestHacksByActivity(count)
        if (requestId === requestIdRef.current) {
          setHacks(result)
        }
      } catch (loadError) {
        if (requestId === requestIdRef.current) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load Michel PD hacks.',
          )
        }
        throw loadError
      } finally {
        if (showInitialLoading && requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    staleAfterMs: refreshPolicy.staleAfterMs,
    pollIntervalMs: refreshPolicy.pollIntervalMs,
    minRefreshGapMs: refreshPolicy.minRefreshGapMs,
    maxFailureBackoffMs: refreshPolicy.maxFailureBackoffMs,
  })

  useEffect(() => {
    if (hasSsrHacks) {
      return
    }
    void triggerRefresh('mount', { force: true })
  }, [hasSsrHacks, triggerRefresh])

  const refresh = useCallback(async () => {
    await triggerRefresh('manual', { force: true })
  }, [triggerRefresh])

  return {
    hacks,
    loading,
    error,
    refresh,
    lastUpdatedAt: lastSuccessAt,
    isRefreshing,
  }
}
